import React from "react";
import { Link, useParams } from "react-router-dom";
import { Button } from "./ui/button";
import Header from "./Header";
import Footer from "./Footer";
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Users,
  Sparkles,
  CheckCircle2,
  Circle,
  MessageCircle,
} from "lucide-react";
import { colors } from "../config/site";
import { caseStudies } from "../data/caseStudies";

/**
 * Case Studies hub — /stories/case-studies
 * Displays all 3 stories as branded journey cards.
 */
export const CaseStudiesHub = () => {
  const accentColor = (a) =>
    a === "primary" ? colors.primary : a === "terracotta" ? colors.terracotta : colors.gold;

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.cream }}>
      <Header />
      <main className="pt-28 pb-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <Link
            to="/resources"
            className="inline-flex items-center text-sm font-semibold mb-6"
            style={{ color: colors.primary }}
            data-testid="cases-back"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Resources
          </Link>

          <div className="max-w-3xl mb-10">
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-4"
              style={{ backgroundColor: colors.terracotta + "22", color: colors.terracottaDark }}
            >
              <BookOpen className="w-3.5 h-3.5" />
              Case Studies
            </div>
            <h1
              className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-4"
              style={{ color: colors.charcoal }}
            >
              Real stories.{" "}
              <span style={{ color: colors.primary }}>Real decisions.</span>
            </h1>
            <p className="text-lg" style={{ color: colors.textMuted }}>
              Adults learn best from stories — not lectures. Each case study drops you into a
              real-life-inspired situation and asks: <em>What would YOU do?</em> Perfect for
              individual reflection or partner-org group workshops.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {caseStudies.map((story) => (
              <Link
                key={story.slug}
                to={`/stories/${story.slug}`}
                className="group rounded-3xl overflow-hidden shadow-md hover:shadow-xl transition-all bg-white flex flex-col"
                data-testid={`case-card-${story.slug}`}
              >
                <div
                  className="h-2"
                  style={{ backgroundColor: accentColor(story.accent) }}
                />
                <div className="p-6 lg:p-7 flex-grow flex flex-col">
                  <div
                    className="inline-block w-fit px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest mb-3"
                    style={{
                      backgroundColor: accentColor(story.accent) + "22",
                      color: accentColor(story.accent),
                    }}
                  >
                    {story.module}
                  </div>
                  <h3 className="text-xl font-bold mb-1" style={{ color: colors.charcoal }}>
                    {story.title}
                  </h3>
                  <p
                    className="text-xs uppercase tracking-wider font-semibold mb-3"
                    style={{ color: colors.textMuted }}
                  >
                    {story.subtitle}
                  </p>
                  <p className="text-sm mb-5 flex-grow" style={{ color: colors.textMuted }}>
                    {story.intro.hook}
                  </p>
                  <span
                    className="inline-flex items-center gap-2 text-sm font-bold group-hover:gap-3 transition-all"
                    style={{ color: accentColor(story.accent) }}
                  >
                    Read {story.intro.name}&apos;s Story <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            ))}
          </div>

          <div
            className="mt-12 rounded-2xl p-5 flex gap-4 items-start"
            style={{ backgroundColor: colors.ivory, border: `1px solid ${colors.gold}55` }}
          >
            <Users className="w-6 h-6 flex-shrink-0 mt-0.5" style={{ color: colors.primary }} />
            <div>
              <p
                className="text-xs uppercase tracking-widest font-bold mb-1"
                style={{ color: colors.sage }}
              >
                For Partner Organizations
              </p>
              <p className="text-sm" style={{ color: colors.charcoal }}>
                Each story ends with discussion questions your team can use in group workshops.
                Contact us to bring these case studies into your programming.
              </p>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

/**
 * Individual Case Study — /stories/:slug
 */
export const CaseStudyDetail = () => {
  const { slug } = useParams();
  const story = caseStudies.find((s) => s.slug === slug);
  const [selected, setSelected] = React.useState(new Set()); // multi-select for "which of these were right?"
  const [revealed, setRevealed] = React.useState(false);

  if (!story) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: colors.cream }}>
        <Header />
        <main className="pt-28 pb-20 px-4 text-center">
          <p style={{ color: colors.charcoal }}>Story not found.</p>
          <Link to="/stories/case-studies" className="text-sm font-semibold" style={{ color: colors.primary }}>
            Back to Case Studies
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const accentColor =
    story.accent === "primary" ? colors.primary : story.accent === "terracotta" ? colors.terracotta : colors.gold;

  const toggle = (idx) => {
    if (revealed) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const bestIdxs = story.choices
    .map((c, i) => (c.isBest ? i : null))
    .filter((v) => v !== null);
  const chosenBestCount = [...selected].filter((i) => story.choices[i].isBest).length;
  const chosenBadCount = [...selected].filter((i) => !story.choices[i].isBest).length;

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.cream }}>
      <Header />
      <main className="pt-28 pb-20 px-4">
        <div className="container mx-auto max-w-3xl">
          <Link
            to="/stories/case-studies"
            className="inline-flex items-center text-sm font-semibold mb-6"
            style={{ color: colors.primary }}
            data-testid="case-back-hub"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            All Case Studies
          </Link>

          <div className="rounded-3xl shadow-lg overflow-hidden bg-white">
            <div className="h-2" style={{ backgroundColor: accentColor }} />
            <div className="p-8 lg:p-12">
              <div
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-4"
                style={{ backgroundColor: accentColor + "22", color: accentColor }}
              >
                <Sparkles className="w-3.5 h-3.5" />
                Case Study · {story.tag}
              </div>
              <h1 className="text-3xl lg:text-4xl font-bold mb-2" style={{ color: colors.charcoal }}>
                {story.title}
              </h1>
              <p className="text-lg italic mb-6" style={{ color: colors.textMuted }}>
                {story.subtitle}
              </p>

              {/* Situation */}
              <div className="rounded-2xl p-5 lg:p-6 mb-6" style={{ backgroundColor: colors.cream }}>
                <p
                  className="text-xs uppercase tracking-widest font-bold mb-3"
                  style={{ color: accentColor }}
                >
                  Meet {story.intro.name}
                </p>
                <ul className="space-y-1.5 mb-4 text-sm" style={{ color: colors.charcoal }}>
                  {story.intro.situation.map((line, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span
                        className="mt-2 w-1.5 h-1.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: accentColor }}
                      />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-sm italic" style={{ color: colors.textMuted }}>
                  {story.intro.hook}
                </p>
              </div>

              {/* Question */}
              <h2 className="text-xl lg:text-2xl font-bold mb-2" style={{ color: colors.charcoal }}>
                {story.question}
              </h2>
              <p className="text-sm mb-5" style={{ color: colors.textMuted }}>
                Select every option you think is a smart move. Then reveal.
              </p>

              <div className="space-y-3 mb-6">
                {story.choices.map((choice, idx) => {
                  const isSelected = selected.has(idx);
                  const showAsGood = revealed && choice.isBest;
                  const showAsBad = revealed && isSelected && !choice.isBest;
                  return (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => toggle(idx)}
                      disabled={revealed}
                      className="w-full text-left rounded-2xl p-4 lg:p-5 border-2 transition-all disabled:cursor-default"
                      style={{
                        backgroundColor: showAsGood
                          ? colors.sage + "1a"
                          : showAsBad
                          ? colors.terracotta + "1a"
                          : isSelected
                          ? colors.primary + "0d"
                          : colors.white,
                        borderColor: showAsGood
                          ? colors.sage
                          : showAsBad
                          ? colors.terracotta
                          : isSelected
                          ? colors.primary
                          : "#E6DFD2",
                      }}
                      data-testid={`case-choice-${idx}`}
                    >
                      <div className="flex items-start gap-3">
                        {revealed ? (
                          choice.isBest ? (
                            <CheckCircle2
                              className="w-5 h-5 mt-0.5 flex-shrink-0"
                              style={{ color: colors.sage }}
                            />
                          ) : isSelected ? (
                            <Circle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: colors.terracotta }} />
                          ) : (
                            <Circle className="w-5 h-5 mt-0.5 flex-shrink-0 opacity-30" />
                          )
                        ) : (
                          <Circle
                            className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                              isSelected ? "fill-current" : "opacity-40"
                            }`}
                            style={{ color: isSelected ? colors.primary : colors.charcoal }}
                          />
                        )}
                        <div className="flex-grow">
                          <p className="font-semibold" style={{ color: colors.charcoal }}>
                            {choice.label}
                          </p>
                          {revealed && (
                            <p
                              className="text-sm mt-2"
                              style={{
                                color: choice.isBest ? colors.primary : colors.terracottaDark,
                              }}
                            >
                              {choice.feedback}
                            </p>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {!revealed && (
                <Button
                  onClick={() => setRevealed(true)}
                  disabled={selected.size === 0}
                  className="rounded-full px-6 py-5 font-semibold text-white shadow-md hover:shadow-xl transition-all"
                  style={{ backgroundColor: accentColor }}
                  data-testid="case-reveal-btn"
                >
                  Reveal What Happened
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              )}

              {revealed && (
                <div className="mt-8 space-y-6" data-testid="case-reveal">
                  {/* Your score summary */}
                  <div
                    className="rounded-2xl p-5"
                    style={{ backgroundColor: colors.ivory, borderLeft: `4px solid ${accentColor}` }}
                  >
                    <p
                      className="text-xs uppercase tracking-widest font-bold mb-2"
                      style={{ color: accentColor }}
                    >
                      Your choices
                    </p>
                    <p className="text-sm" style={{ color: colors.charcoal }}>
                      You picked <strong>{chosenBestCount} of {bestIdxs.length}</strong> smart moves
                      {chosenBadCount > 0 ? (
                        <> and <strong>{chosenBadCount}</strong> option{chosenBadCount === 1 ? "" : "s"} to reconsider.</>
                      ) : (
                        <>. Nice work.</>
                      )}
                    </p>
                  </div>

                  {/* What happened */}
                  <div>
                    <h3
                      className="text-xl font-bold mb-3"
                      style={{ color: colors.charcoal }}
                    >
                      What {story.intro.name} Actually Did
                    </h3>
                    <p className="mb-3 text-base" style={{ color: colors.charcoal }}>
                      {story.reveal.whatHappened}
                    </p>
                    <p className="text-base" style={{ color: colors.charcoal }}>
                      {story.reveal.outcome}
                    </p>
                  </div>

                  {/* Key lessons */}
                  <div>
                    <h3
                      className="text-xl font-bold mb-3"
                      style={{ color: colors.charcoal }}
                    >
                      Key Lessons
                    </h3>
                    <ul className="space-y-2">
                      {story.reveal.keyLessons.map((lesson, i) => (
                        <li
                          key={i}
                          className="flex items-start gap-2 text-sm"
                          style={{ color: colors.charcoal }}
                        >
                          <CheckCircle2
                            className="w-4 h-4 mt-0.5 flex-shrink-0"
                            style={{ color: colors.sage }}
                          />
                          <span>{lesson}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Discussion questions */}
                  <div
                    className="rounded-2xl p-6"
                    style={{ backgroundColor: colors.cream }}
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <MessageCircle
                        className="w-5 h-5"
                        style={{ color: colors.terracotta }}
                      />
                      <h3 className="text-lg font-bold" style={{ color: colors.charcoal }}>
                        Discussion Questions
                      </h3>
                    </div>
                    <p className="text-xs italic mb-4" style={{ color: colors.textMuted }}>
                      Great for group workshops or personal reflection.
                    </p>
                    <ol className="space-y-3 list-decimal list-inside text-sm" style={{ color: colors.charcoal }}>
                      {story.discussion.map((q, i) => (
                        <li key={i}>{q}</li>
                      ))}
                    </ol>
                  </div>

                  {/* CTA */}
                  <div
                    className="rounded-3xl p-6 text-white"
                    style={{
                      background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})`,
                    }}
                  >
                    <h3 className="text-xl font-bold mb-2">
                      Ready to write your own success story?
                    </h3>
                    <p className="text-sm opacity-90 mb-5">
                      Our workshops walk families and individuals through moments just like this
                      one — with tools, tips, and coaching.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Link to="/programs">
                        <Button
                          className="rounded-full px-6 py-5 font-semibold w-full sm:w-auto"
                          style={{ backgroundColor: colors.terracotta, color: colors.white }}
                        >
                          See Our Programs
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </Button>
                      </Link>
                      <Link to="/stories/case-studies">
                        <Button className="rounded-full px-6 py-5 font-semibold w-full sm:w-auto border-white/40 border bg-transparent hover:bg-white/10">
                          Read Another Story
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};
